import React, { useRef, useState, type FunctionComponent } from 'react'
import styled from '@emotion/styled'
import { DraggableCore, type DraggableEventHandler } from 'react-draggable'
import { type Styles } from 'react-modal'
import moment from 'moment'
import { RefreshIcon } from '@instacart/ids-core'
import { Tooltip, colors } from '@retailer-platform/shared-components'
import { withinDates } from 'legacy/common/dateUtils'
import { addInterval, intervalDifference } from 'domains/shifts/components/utils/intervalUtils'
import { isInPast } from 'legacy/common/typedDateUtils'
import FormattedTimeWithOffset from 'legacy/components/FormattedTimeWithOffset'
import LoadingDots from 'legacy/components/LoadingDots'
import StandardError from 'legacy/common/errors/StandardError'
import StyledCell from 'domains/shifts/components/StyledCell'
import {
  type ShiftManagementContextProps,
  withShiftManagementContext,
} from 'domains/shifts/components/contexts/withShiftManagementContext'
import {
  type NotificationsContextValue,
  withNotificationsContext,
} from 'legacy/contexts/notifications/NotificationsContext'
import { useShiftsStateContext } from 'domains/shifts/components/contexts/ShiftsStateContext'
import { useDomainMessages } from 'domains/shifts/utils/domain/intl'
import { useShiftManagementContext } from 'domains/shifts/components/contexts/ShiftManagementContext'
import { getPartnerPickRecurringShiftsFormData } from './recurring-shifts/partnerPickRecurringShiftForm.utils'
import { calculateShiftModalStyles } from './utils/calculateShiftModalStyles'

const DRAG_MARGIN = 5

const Handle = styled.div<{ disabled?: boolean }>(({ disabled }) => ({
  position: 'absolute',
  cursor: disabled ? 'not-allowed' : 'col-resize',
  top: 0,
  bottom: 0,
  width: '26px',
  zIndex: 1,
}))

const LeftHandle = styled(Handle)({
  left: 0,
})

const RightHandle = styled(Handle)({
  right: 0,
})

interface BodyContainerProps {
  disabled: boolean
}

const BodyContainer = styled.div<BodyContainerProps>(({ disabled }) => ({
  height: '26px',
  width: 'calc(100% - 30px)',
  position: 'relative',
  left: '15px',
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  cursor: !disabled ? 'pointer' : 'default',
  overflow: 'hidden',
}))

const ShiftInfoContainer = styled.div({
  display: 'flex',
  flex: 1,
  alignItems: 'center',
  justifyContent: 'center',
})

const CellText = styled.div({
  color: colors.GRAYSCALE.X0,
  fontWeight: 600,
  fontSize: '11px',
  display: 'flex',
  alignItems: 'center',
})

const StyledIcon = styled.div({
  display: 'flex',
  alignItems: 'baseline',
  marginRight: '5px',
})

const OnCallLabel = styled.div({
  background: colors.GRAYSCALE.X0,
  display: 'flex',
  alignItems: 'center',
  color: '#72767e',
  padding: '3px 8px',
  height: '24px',
  borderRadius: '4px',
  fontSize: '12px',
})

export interface ShiftCellProps extends ShiftManagementContextProps, NotificationsContextValue {
  id?: number
  start: Date
  end: Date
  zoneOffset: number
  rangeStart: Date
  rangeEnd: Date
  minHour: Date
  maxHour: Date
  disabled: boolean
  disabledComponent: React.ReactNode
  busy?: boolean
  draggable: boolean
  isRecurring: boolean
  recurringShiftId?: number
  shopperId: number
  daysOfWeek: number[]
  recurStartDate: string
  recurEndDate: string
  onCall?: boolean
  isPlaceholder: boolean
  devicePrioritizationEnabled: boolean
  endLabelTime?: Date
  ultrafastCrossDayDeliveryEnabled?: boolean
}

interface State {
  draggedSide?: 'left' | 'right'
  startOffset: number
  endOffset: number
}

const ShiftCell: FunctionComponent<ShiftCellProps> = props => {
  const {
    storeHours: { actualStoreOpen, actualStoreClose },
  } = useShiftManagementContext()
  const [state, setState] = useState<State>({
    draggedSide: undefined,
    startOffset: 0,
    endOffset: 0,
  })
  const componentRef = useRef(null)
  const messages = useDomainMessages({
    onCallLabel: 'shiftsDomain.allocationType.oncall.label',
    recurringLabel: 'shiftsDomain.recurring',
  })

  const { openShiftsModal, setShiftsState } = useShiftsStateContext()

  const renderTimeRange = (start: Date, end: Date, zoneOffset: number) => (
    <React.Fragment>
      <span>
        <FormattedTimeWithOffset value={start} offset={zoneOffset} />
      </span>

      <span style={{ margin: '0 3px' }}>-</span>

      <span>
        <FormattedTimeWithOffset value={end} offset={zoneOffset} />
      </span>
    </React.Fragment>
  )
  const startDragLeft = () => {
    setState(prevState => ({ ...prevState, draggedSide: 'left' }))
  }

  const startDragRight = () => {
    setState(prevState => ({ ...prevState, draggedSide: 'right' }))
  }

  const onDrag: DraggableEventHandler = (_, data) => {
    const { startOffset, endOffset, draggedSide } = state
    const { maxHour, minHour } = props
    const width = (componentRef && componentRef.current && componentRef.current.scrollWidth) || 0
    const columnSpan = getIntervalWidth()
    const segmentWidth = width / columnSpan
    const currentDragDistance = draggedSide === 'right' ? data.x - width : data.x
    const minDragDistance = segmentWidth / 2 + DRAG_MARGIN

    if (Math.abs(currentDragDistance) > minDragDistance) {
      if (draggedSide === 'right') {
        // ensure we never shrink to 0 span
        if (columnSpan === 1 && currentDragDistance < 0) return
        const newOffset = endOffset + (currentDragDistance > 0 ? 1 : -1)
        if (getNewEndTime(newOffset).time > maxHour) return
        setState(prevState => ({ ...prevState, endOffset: newOffset }))
      } else {
        // ensure we never shrink to 0 span
        if (columnSpan === 1 && currentDragDistance > 0) return
        const newOffset = startOffset + (currentDragDistance > 0 ? 1 : -1)
        if (getNewStartTime(newOffset).time < minHour) return
        setState(prevState => ({ ...prevState, startOffset: newOffset }))
      }
    }
  }

  const endDrag = () => {
    const { startOffset, endOffset } = state

    if (startOffset === 0 && endOffset === 0) return

    changeShift()

    setState(prevState => ({
      ...prevState,
      draggedSide: undefined,
    }))
  }

  const handleChangeShiftViaShiftsModal = (newStartTime, newEndTime, onClose) => {
    const { id, recurringShiftId, daysOfWeek, recurStartDate, recurEndDate, zoneOffset, onCall } =
      props
    const formData = getPartnerPickRecurringShiftsFormData(
      zoneOffset,
      newStartTime,
      newEndTime,
      daysOfWeek,
      onCall,
      recurStartDate,
      recurEndDate
    )

    setShiftsState(prevState => ({ ...prevState, isOpenedByDragging: true }))
    // Sometimes when dragging the cell, `handleShiftCellClick` would be invoked accidentally at the end of dragging
    // which results a bug where the shift modal has the `save` button being disabled and doesn't reset the cell after closing it
    // Use `setTimeout` to put `openShiftsModal` in next eventloop tick so we can make sure `openShiftsModal` is called
    // after `handleShiftCellClick` got invoked to avoid the bug
    setTimeout(() => {
      openShiftsModal({
        isRecommendationMode: false,
        recommendationType: null,
        formData,
        shiftId: id,
        recurringShiftId,
        onClose,
        isEditMode: true,
        modalStyles: calculateShiftModalStyles(componentRef) as Styles,
        shopperId: props.shopperId,
      })
    }, 0)
  }

  const changeShift = async (): Promise<void> => {
    const { startOffset, endOffset } = state

    const { time: newStartTime } = getNewStartTime(startOffset)
    const { time: newEndTime } = getNewEndTime(endOffset)

    handleChangeShiftViaShiftsModal(newStartTime, newEndTime, () => resetDraggableCell())
  }

  /// This is also called when changes are made by dragging the shift but they are not saved because the shifts modal is closed/cancelled.
  const resetDraggableCell = () => {
    setState({
      draggedSide: undefined,
      startOffset: 0,
      endOffset: 0,
    })
  }

  const renderLeftHandle = (disabled: boolean) => (
    <DraggableCore disabled={disabled} onStart={startDragLeft} onDrag={onDrag} onStop={endDrag}>
      <LeftHandle disabled={disabled} />
    </DraggableCore>
  )

  const renderRightHandle = (disabled: boolean) => (
    <DraggableCore disabled={disabled} onStart={startDragRight} onDrag={onDrag} onStop={endDrag}>
      <RightHandle disabled={disabled} />
    </DraggableCore>
  )

  const getIntervalWidth = () => {
    const { start, end } = props
    const { startOffset, endOffset } = state

    return intervalDifference(start, end) - startOffset + endOffset
  }

  const getFirstColumn = () => {
    const { rangeStart, start } = props
    const { startOffset } = state

    return intervalDifference(rangeStart, start) + 1 + startOffset
  }

  const getLastColumn = () => {
    const { rangeStart, end } = props
    const { endOffset } = state

    return intervalDifference(rangeStart, end) + 1 + endOffset
  }

  const getNewStartTime = (offset = 0) => {
    const { start } = props
    const newStartTime = addInterval(start, offset)
    const { draggedSide } = state

    // We have two cases where we can't trust user input – when we have
    // "special" hours OR when we're editing current shifts
    if (draggedSide === 'left') {
      // If our "special" hours differ, we should cap shift length to
      // only start as early as our actual open hours
      if (actualStoreOpen && actualStoreOpen.getTime() > newStartTime.getTime()) {
        return { time: actualStoreOpen, errorMessage: 'shiftsDomain.createFailed.invalidHours' }
      }
      // If a user attempts to set a current shift's start time in the past,
      // we should prevent them
      if (newStartTime < moment().toDate()) {
        return { time: start, errorMessage: 'shiftsDomain.createFailed.past' }
      }
    }

    return { time: newStartTime, errorMessage: undefined }
  }

  const getStartTimeWithDrag = () => {
    const { start } = props
    const { startOffset } = state
    return addInterval(start, startOffset)
  }

  const getNewEndTime = (offset = 0) => {
    const { end } = props
    const newEndTime = addInterval(end, offset)
    const { draggedSide } = state

    if (draggedSide === 'right') {
      if (actualStoreClose && end.getTime() > actualStoreClose.getTime()) {
        return { time: actualStoreClose, errorMessage: 'shiftsDomain.createFailed.invalidHours' }
      }

      if (newEndTime < moment().toDate()) {
        return { time: end, errorMessage: 'shiftsDomain.createFailed.past' }
      }
    }

    return { time: newEndTime, errorMessage: undefined }
  }

  const getEndTimeWithDrag = () => {
    const { end, endLabelTime } = props
    const { endOffset } = state
    const displayEndTime = endLabelTime || end
    return addInterval(displayEndTime, endOffset)
  }

  const renderBusy = () => (
    <LoadingDots testId="shopper-shifts-loading-dots" height={10} fill={colors.GRAYSCALE.X0} />
  )

  const handleShiftCellClick = () => {
    const {
      id,
      recurringShiftId,
      start,
      end,
      disabled,
      daysOfWeek,
      recurStartDate,
      recurEndDate,
      zoneOffset,
      onCall,
    } = props
    if (disabled || isInPast(end)) {
      return
    }
    const formData = getPartnerPickRecurringShiftsFormData(
      zoneOffset,
      start,
      end,
      daysOfWeek,
      onCall,
      recurStartDate,
      recurEndDate
    )

    openShiftsModal({
      isRecommendationMode: false,
      recommendationType: null,
      formData,
      shiftId: id,
      recurringShiftId: recurringShiftId,
      modalStyles: calculateShiftModalStyles(componentRef) as Styles,
      isEditMode: true,
      shopperId: props.shopperId,
    })
  }

  const renderBodyContent = (showTooltip: boolean) => {
    const {
      busy,
      disabled,
      disabledComponent,
      zoneOffset,
      isRecurring,
      onCall,
      devicePrioritizationEnabled,
    } = props
    const start = getStartTimeWithDrag()
    const end = getEndTimeWithDrag()

    return (
      <BodyContainer disabled={disabled}>
        {devicePrioritizationEnabled && onCall && getIntervalWidth() > 6 && (
          <OnCallLabel>{messages.onCallLabel}</OnCallLabel>
        )}

        <ShiftInfoContainer>
          <CellText>
            {isRecurring && (
              <StyledIcon>
                <RefreshIcon
                  color="systemGrayscale00"
                  size={15}
                  data-testid="recurring-shift-icon"
                />
              </StyledIcon>
            )}

            {showTooltip ? (
              <span>...</span>
            ) : disabled ? (
              disabledComponent
            ) : busy ? (
              renderBusy()
            ) : (
              renderTimeRange(start, end, zoneOffset)
            )}
          </CellText>
        </ShiftInfoContainer>
      </BodyContainer>
    )
  }

  const renderBody = () => {
    const { zoneOffset, isRecurring, onCall, devicePrioritizationEnabled } = props
    const start = getStartTimeWithDrag()
    const end = getEndTimeWithDrag()

    if (devicePrioritizationEnabled && onCall && getIntervalWidth() <= 8) {
      // if this is an oncall shift and shift length is less than 2 hours, the shift info doesn't fit in the container
      // so we truncate it and display a tooltip on hover
      return (
        <Tooltip placement="top" target={renderBodyContent(true)}>
          <p>{renderTimeRange(start, end, zoneOffset)}</p>
          <p>
            {[messages.onCallLabel, isRecurring && messages.recurringLabel.toLocaleLowerCase()]
              .filter(Boolean)
              .join(', ')}
          </p>
        </Tooltip>
      )
    } else {
      return renderBodyContent(false)
    }
  }

  const {
    disabled,
    draggable,
    id,
    start,
    end,
    rangeStart,
    rangeEnd,
    isRecurring,
    isPlaceholder,
    ultrafastCrossDayDeliveryEnabled,
  } = props

  // To support shift crossing midnight,
  // we do not check if the end date is within the business hours when ultrafastCrossDayDeliveryEnabled flipper is enabled

  const isWithInBusinessHour = ultrafastCrossDayDeliveryEnabled
    ? withinDates(start, rangeStart, rangeEnd)
    : withinDates(start, rangeStart, rangeEnd) && withinDates(end, rangeStart, rangeEnd)
  if (!isWithInBusinessHour) {
    throw new StandardError('Invalid shift length', {
      id,
      start,
      end,
      rangeStart,
      rangeEnd,
    })
  }

  const isStartInPast = isInPast(start)
  const isEndInPast = isInPast(end)
  return (
    <StyledCell
      data-testid="shopper-shifts-shift-cell"
      ref={componentRef}
      disabled={disabled}
      firstColumn={getFirstColumn()}
      lastColumn={getLastColumn()}
      isRecurring={isRecurring}
      isInactive={isEndInPast}
      enhancedRecurringShiftsEnabled
      isPlaceholder={isPlaceholder}
      onClick={handleShiftCellClick}
    >
      {!disabled && draggable && renderLeftHandle(isStartInPast)}
      {renderBody()}
      {!disabled && draggable && renderRightHandle(isEndInPast)}
    </StyledCell>
  )
}

export default withNotificationsContext(withShiftManagementContext(ShiftCell))
